/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */
import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import {
  Content,
  Header,
  InfoCard,
  Page,
  Progress,
  UserIdentity,
} from '@backstage/core-components';
import {
  type IdentityApi,
  type SignInPageProps,
  configApiRef,
  errorApiRef,
  useAnalytics,
  useApi,
} from '@backstage/core-plugin-api';
import { useSearchParams } from 'react-router-dom';
import { m42EnterpriseAuthApiRef, m42EnterpriseProviderConfig } from './m42EnterpriseAuth';

// PLAT-3071: Microsoft is the only sign-in; Google stays configured in the backend
// but its card is shown disabled. Backstage's SignInPage cannot render a disabled
// provider, so this mirrors its MultiSignInPage and common provider for the two
// cards: the same storage key, so a browser that last used Microsoft is restored
// silently while one that last used Google lands here and must pick Microsoft.
export const PROVIDER_STORAGE_KEY = '@backstage/core:SignInPage:provider';

const useStyles = makeStyles({
  container: { padding: 0, listStyle: 'none' },
  item: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '400px',
    margin: 0,
    padding: 0,
  },
});

// The text Backstage's sign-in page posts (a ForwardedError), without that dependency.
const loginFailed = (cause: unknown) =>
  new Error(
    `Login failed; caused by ${cause instanceof Error ? `${cause.name}: ${cause.message}` : String(cause)}`,
  );

// Clears the remembered provider on sign-out, as Backstage's own sign-in page does.
const withSignOutReset = (identityApi: IdentityApi, onSignOut: () => void): IdentityApi => ({
  getProfileInfo: () => identityApi.getProfileInfo(),
  getBackstageIdentity: () => identityApi.getBackstageIdentity(),
  getCredentials: () => identityApi.getCredentials(),
  signOut: async () => {
    localStorage.removeItem(PROVIDER_STORAGE_KEY);
    await identityApi.signOut();
    onSignOut();
  },
});

export const M42SignInPage = ({ onSignInSuccess }: SignInPageProps) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const errorApi = useApi(errorApiRef);
  const authApi = useApi(m42EnterpriseAuthApiRef);
  const analytics = useAnalytics();
  const [searchParams] = useSearchParams();
  const [restoring, setRestoring] = useState(
    () => localStorage.getItem(PROVIDER_STORAGE_KEY) === m42EnterpriseProviderConfig.id,
  );

  const complete = async (identity: Awaited<ReturnType<typeof authApi.getBackstageIdentity>>) => {
    if (!identity) return false;
    const profile = await authApi.getProfile();
    onSignInSuccess(
      withSignOutReset(UserIdentity.create({ identity: identity.identity, profile, authApi }), () =>
        analytics.captureEvent('signOut', 'success'),
      ),
    );
    analytics.captureEvent('signIn', 'success');
    return true;
  };

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam) {
      errorApi.post(loginFailed(new Error(errorParam)));
    }
    // Only on mount, like Backstage's sign-in page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!restoring) return undefined;
    let cancelled = false;
    authApi
      .getBackstageIdentity({ optional: true })
      .then(async identity => {
        if (!cancelled && !(await complete(identity))) setRestoring(false);
      })
      .catch(error => {
        if (cancelled) return;
        localStorage.removeItem(PROVIDER_STORAGE_KEY);
        errorApi.post(error);
        setRestoring(false);
      });
    return () => {
      cancelled = true;
    };
    // Once, on mount: a later failure shows the page rather than retrying.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signInWithMicrosoft = async () => {
    try {
      localStorage.setItem(PROVIDER_STORAGE_KEY, m42EnterpriseProviderConfig.id);
      const identity = await authApi.getBackstageIdentity({ instantPopup: true });
      if (!(await complete(identity))) {
        throw new Error(`The ${m42EnterpriseProviderConfig.title} provider is not configured to support sign-in`);
      }
    } catch (error) {
      localStorage.removeItem(PROVIDER_STORAGE_KEY);
      errorApi.post(loginFailed(error));
    }
  };

  if (restoring) {
    return <Progress />;
  }

  return (
    <Page themeId="home">
      <Header title={configApi.getString('app.title')} />
      <Content>
        <Grid container justifyContent="flex-start" spacing={2} component="ul" classes={classes}>
          <Grid component="li" item classes={classes}>
            <InfoCard
              variant="fullHeight"
              title={m42EnterpriseProviderConfig.title}
              titleTypographyProps={{ component: 'h2' }}
              actions={
                <Button
                  color="primary"
                  variant="outlined"
                  aria-label={`Sign in with ${m42EnterpriseProviderConfig.title}`}
                  onClick={signInWithMicrosoft}
                >
                  Sign In
                </Button>
              }
            >
              <Typography variant="body1">{m42EnterpriseProviderConfig.message}</Typography>
            </InfoCard>
          </Grid>
          <Grid component="li" item classes={classes}>
            <InfoCard
              variant="fullHeight"
              title="Google"
              titleTypographyProps={{ component: 'h2' }}
              actions={
                <Button
                  color="primary"
                  variant="outlined"
                  aria-label="Sign in with Google (turned off)"
                  aria-describedby="m42-google-turned-off"
                  disabled
                >
                  Sign In
                </Button>
              }
            >
              <Typography id="m42-google-turned-off" variant="body1" color="textSecondary">
                Google sign-in is turned off. Sign in with your Mission 42 Microsoft account.
              </Typography>
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
